import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { REGULATED_PANEL_STYLES } from './sales-regulated-panel.styles';
import { RegulatedComplianceCaseRecord, SalesRegulatedApiService } from './sales-regulated-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-compliance-cases',
  imports: [AinPageComponent, AinStateComponent, FormsModule, TableModule, ButtonModule, CheckboxModule, InputTextModule, AinDatePipe],
  template: `
    <ain-page [title]="t('sales.regulated.compliance.title')" [description]="t('sales.regulated.compliance.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('sales.regulated.compliance.runScreen')" (click)="openScreen()"></button>
            <button pButton type="button" [label]="t('sales.regulated.compliance.recordSuitability')" severity="secondary" (click)="openSuitability()"></button>
            <button pButton type="button" [label]="t('sales.action.edit')" severity="secondary" [disabled]="selected().length !== 1" (click)="openSelected()"></button>
            <button pButton type="button" [label]="t('sales.action.refresh')" severity="secondary" (click)="load()"></button>
          </div>
          <div class="filters" [class.collapsed]="!filtersOpen()">
            <button type="button" class="filter-toggle" (click)="filtersOpen.set(!filtersOpen())">{{ t('sales.regulated.filters.title') }}</button>
            <div class="filter-body filter-grid">
              <label><span>{{ t('sales.regulated.compliance.fields.screening') }}</span><input pInputText [(ngModel)]="filterScreening" (ngModelChange)="applyFilters()" /></label>
            </div>
          </div>
          <p-table [value]="filtered()" dataKey="id" [paginator]="true" [rows]="20" [selection]="selected()" (selectionChange)="onSelect($event)">
            <ng-template pTemplate="header">
              <tr>
                <th style="width:44px"><p-tableHeaderCheckbox /></th>
                <th>{{ t('sales.regulated.compliance.fields.screening') }}</th>
                <th>{{ t('sales.regulated.compliance.fields.result') }}</th>
                <th>{{ t('sales.regulated.compliance.fields.hardStop') }}</th>
                <th>{{ t('columns.createdAt') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.screeningType }}</button></td>
                <td>{{ row.result }}</td>
                <td>{{ row.isHardStop ? t('common.yes') : t('common.no') }}</td>
                <td>{{ row.createdAtUtc | ainDate:'datetime' }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [REGULATED_PANEL_STYLES]
})
export class SalesComplianceCasesComponent implements OnInit {
  private readonly api = inject(SalesRegulatedApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly rows = signal<RegulatedComplianceCaseRecord[]>([]);
  readonly filtered = signal<RegulatedComplianceCaseRecord[]>([]);
  readonly selected = signal<RegulatedComplianceCaseRecord[]>([]);
  readonly filtersOpen = signal(true);
  filterScreening = '';
  ngOnInit(): void { this.load(); }
  load(): void {
    this.loading.set(true);
    this.api.listComplianceCases().subscribe({
      next: r => { this.rows.set(r); this.applyFilters(); this.loading.set(false); },
      error: () => { this.rows.set([]); this.loading.set(false); }
    });
  }
  applyFilters(): void {
    const s = this.filterScreening.trim().toUpperCase();
    this.filtered.set(this.rows().filter(r => !s || r.screeningType.toUpperCase().includes(s)));
  }
  onSelect(v: RegulatedComplianceCaseRecord[]): void { this.selected.set(v ?? []); }
  openScreen(): void { void this.router.navigate(['/sales/compliance-cases/new']); }
  openSuitability(): void { void this.router.navigate(['/sales/compliance-cases/suitability']); }
  openSelected(): void { const id = this.selected()[0]?.id; if (id) this.openDetail(id); }
  openDetail(id: string): void { void this.router.navigate(['/sales/compliance-cases', id]); }
  t(key: string): string { return this.localization.translate(key); }
}
